import { SdkContext } from '../internal'
import type { SignedMediaUrl } from './media'

/** F-178: the two space modules that share the media show engine. */
export type MediaShowModule = 'podcast' | 'videos'
export type MediaSpaceMode = 'single' | 'multi'
export type MediaEpisodeType = 'full' | 'trailer' | 'bonus'
export type MediaEpisodeStatus = 'draft' | 'scheduled' | 'published'
export type MediaKind = 'audio' | 'video'

export interface MediaProfile {
  id: string
  name: string
  avatar?: string | null
  role?: string
}

export interface MediaPresenter {
  memberId: string
  member?: MediaProfile | null
  addedAt?: string
}

/**
 * F-178 1b: what opens a locked show or episode for who reads: `product` (the show sold in the
 * Store; `subscriptionGroupIds` are the public plans that include it), `plan`, `group`, `level`,
 * `badge` or `other`; `actions` are the screen's hints (`buy`, `seePlans`, `askTeam`…).
 */
export interface MediaAccessOffer {
  reason: string
  actions: string[]
  productIds?: string[]
  subscriptionGroupIds?: string[]
}

/** F-178 1b: the Store product that sells a show (born with it, a free draft). */
export interface MediaShowProductSummary {
  id: string
  status: 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'BLOCKED'
  price: string
  currency: string
  pricingMode: 'MONEY' | 'POINTS' | 'EITHER'
  pointsPrice: number | null
  accessDuration: number | null
  slug: string
}

export interface MediaShowProduct extends MediaShowProductSummary {
  kind: 'show'
  mediaShowId: string
  title: string
  includedInPlanIds: string[]
}

/** F-178 1b: an episode posted in the Feed. Never a media link: `full`/`open` play through the episode. */
export interface MediaEpisodeCard {
  id: string
  title: string
  showId: string
  showTitle: string | null
  spaceId: string
  type: MediaEpisodeType
  isOpen: boolean
  mediaKind: MediaKind | null
  durationSeconds: number | null
  publishedAt: string | null
  coverUrl: string | null
  access: 'full' | 'open' | 'locked' | 'unavailable'
  offer?: MediaAccessOffer | null
}

export interface MediaShow {
  id: string
  spaceId: string
  title: string
  slug: string
  description: string | null
  coverMediaId: string | null
  category: string | null
  language: string | null
  explicit: boolean
  authorName: string | null
  /** Podcast: each episode is transcribed when it goes on air (on by default). */
  autoTranscribe: boolean
  position: number
  presenters: MediaPresenter[]
  /** Episodes on air. */
  episodeCount: number
  /** Owner or admin: edits and deletes the show and picks its presenters. */
  canManage: boolean
  /** Owner, admin or a presenter of this show: creates, edits, publishes and deletes episodes. */
  canPublish: boolean
  /** F-178 1b: `locked` = sold as a product that who reads has neither bought nor has by plan. */
  access?: 'full' | 'locked'
  product?: MediaShowProductSummary | null
  offer?: MediaAccessOffer | null
  /** F-178 phase 2: `off` (no RSS), `private` (each person with access gets their own link) or `public` (also an open feed). */
  rssMode?: MediaRssMode
  feedType?: MediaFeedType
  /** The iframe embed of the show and its episodes. */
  embedEnabled?: boolean
  /** The public feed links (only with `rssMode: public`). */
  feedUrls?: { audio: string; video: string } | null
  /** Owner and admin only: the owner e-mail the public feed carries. */
  ownerEmail?: string | null
  /** Who publishes, in a show imported by RSS: the feed it came from and the copy of its audio files. */
  importedFromUrl?: string | null
  importedAt?: string | null
  import?: MediaShowImportState | null
}

export type MediaRssMode = 'off' | 'private' | 'public'
export type MediaFeedType = 'episodic' | 'serial'

export interface MediaShowImportState {
  status: 'importing' | 'done'
  total: number
  pending: number
  done: number
  failed: number
}

/** `POST /media-shows/import`: the show born from another host's feed (episodes as drafts, audio copied after). */
export interface MediaShowImportResult {
  show: Pick<MediaShow, 'id' | 'title' | 'rssMode' | 'import'>
  import: { episodes: number; skipped: number; warnings: string[] }
}

/** "Meu link do feed": the person's own feed link (a secret; another one invalidates it). */
export interface MediaMyFeed {
  showId: string
  feedUrl: string
  /** null until a video episode is live (`hasVideo`). */
  videoFeedUrl: string | null
  hasVideo: boolean
  rotated?: boolean
  createdAt: string
  lastUsedAt: string | null
}

export interface MediaShowsSpace {
  id: string
  module: MediaShowModule
  mode: MediaSpaceMode
  canCreateShow: boolean
}

export interface MediaShowInput {
  title?: string
  description?: string | null
  coverMediaId?: string | null
  category?: string | null
  language?: string | null
  explicit?: boolean
  authorName?: string | null
  autoTranscribe?: boolean
  position?: number
  rssMode?: MediaRssMode
  feedType?: MediaFeedType
  ownerEmail?: string | null
  embedEnabled?: boolean
}

export interface MediaChapter {
  startSeconds: number
  title: string
}

export interface MediaTranscriptState {
  status: 'pending' | 'ready' | 'failed' | 'quotaExceeded' | null
  language?: string | null
  updatedAt?: string | null
  /** Only for who edits: there is a file and no transcript on its way. */
  canTranscribe?: boolean
}

export interface MediaReactionCount {
  type: string
  count: number
  reacted: boolean
}

export interface MediaEpisodeProgress {
  /** Where to resume; `null` starts over. */
  resumeAt: number | null
  maxPositionSeconds?: number
  completed?: boolean
  completedAt?: string | null
}

export interface MediaEpisode {
  id: string
  showId: string
  spaceId: string
  title: string
  description: string | null
  season: number | null
  number: number | null
  type: MediaEpisodeType
  /** Plays for every member of the community, also in a paid show. */
  isOpen: boolean
  status: MediaEpisodeStatus
  /** On air now (published, or scheduled and past its time). */
  live: boolean
  publishAt: string | null
  publishedAt: string | null
  durationSeconds: number | null
  mediaKind: MediaKind | null
  audioMediaId: string | null
  videoMediaId: string | null
  coverMediaId: string | null
  chapters: MediaChapter[]
  chaptersSource: 'editor' | 'description' | null
  /** Only for who edits: the chapters saved by the editor (`null` = read from the description). */
  editorChapters?: MediaChapter[] | null
  /** `locked` (F-178 1b): a show sold as a product who reads lacks; no file ids, see `offer`. */
  access: 'full' | 'open' | 'locked'
  offer?: MediaAccessOffer | null
  /** Only for who edits: the event the recording came from. */
  sourceEventId?: string | null
  transcript?: MediaTranscriptState | null
  reactions: MediaReactionCount[]
  commentCount: number
  progress?: MediaEpisodeProgress | null
  canEdit: boolean
  canDelete: boolean
  canPublish: boolean
  canManageShow: boolean
  /** The version read (F-174): sent back in `updatedAt` on `PUT`, 409 when someone saved after it. */
  updatedAt?: string
}

export interface MediaEpisodeNeighbour {
  id: string
  title: string
  season: number | null
  number: number | null
}

/** `GET /media-episodes/{id}`: the episode with its signed links (one hour) and its neighbours in the show. */
export interface MediaEpisodeDetail extends MediaEpisode {
  show: { id: string; title: string; coverMediaId: string | null; spaceId: string }
  /** `removed`: the file was deleted from the Media library ("mídia removida"). */
  media: { audio: SignedMediaUrl | null; video: SignedMediaUrl | null; cover: SignedMediaUrl | null; removed?: boolean }
  previous: MediaEpisodeNeighbour | null
  next: MediaEpisodeNeighbour | null
}

export interface MediaEpisodeInput {
  title?: string
  description?: string | null
  season?: number | null
  number?: number | null
  type?: MediaEpisodeType
  isOpen?: boolean
  audioMediaId?: string | null
  videoMediaId?: string | null
  coverMediaId?: string | null
  durationSeconds?: number | null
  /** Override the description's; `null` reads the description again. */
  chapters?: MediaChapter[] | null
  /** `PUT` only (F-174): the version read; another save after it answers 409. */
  updatedAt?: string
}

export interface MediaPagination {
  page: number
  limit?: number
  total: number
  totalPages: number
}

export interface MediaEpisodePage {
  items: MediaEpisode[]
  pagination: MediaPagination | null
  seasons: number[]
}

export interface MediaTranscript {
  episodeId: string
  status: 'ready'
  language: string | null
  text: string
  cues: Array<{ start: number; end: number; text: string }>
  vtt: string
  updatedAt: string
}

export interface MediaEpisodeStatsRow {
  episodeId: string
  title?: string
  status?: MediaEpisodeStatus
  publishedAt?: string | null
  mediaKind: MediaKind | null
  durationSeconds: number | null
  plays: number
  playsBySource?: { app: number; embed: number; rss: number }
  /** F-178 phase 2: downloads by the podcast apps (RSS), apart from the app's and the embed's plays. */
  rssDownloads?: number
  uniqueListeners: number
  starters: number
  completions: number
  /** 0 to 1; `null` with no known length or nobody yet. */
  averageCompletion: number | null
}

export interface MediaEpisodeStats extends MediaEpisodeStatsRow {
  showId: string
  retention: {
    segmentCount: number
    sessions: number
    segments: Array<{ index: number; startSeconds: number | null; endSeconds: number | null; sessions: number; share: number }>
  }
}

export interface MediaShowStats {
  showId: string
  totals: { episodes: number; plays: number; uniqueListeners: number; completions: number; averageCompletion: number | null; rssDownloads?: number }
  episodes: MediaEpisodeStatsRow[]
}

export interface MediaEpisodeComment {
  id: string
  content: string
  videoMediaId?: string | null
  author: MediaProfile
  parentId?: string | null
  reactions: MediaReactionCount[]
  canEdit?: boolean
  canDelete?: boolean
  replies?: MediaEpisodeComment[]
  createdAt: string
  updatedAt?: string
}

export interface MediaProgressInput {
  positionSeconds: number
  durationSeconds?: number
  sessionId?: string
  listenedSeconds?: number
}

const itemsOf = <T>(result: unknown): { items: T[]; pagination: MediaPagination | null } => {
  if (Array.isArray(result)) return { items: result as T[], pagination: null }
  const r = result as { items?: T[]; data?: T[]; pagination?: MediaPagination } | null
  return { items: r?.items || r?.data || [], pagination: r?.pagination ?? null }
}

/**
 * F-178 Podcast and Vídeos: shows (`/media-shows`) and their episodes
 * (`/media-episodes`). The community comes from X-CommunityId; paths are
 * written by hand (F-03).
 */
export class MediaShowsResource {
  constructor(private ctx: SdkContext) {}

  /** The shows of a space, with its mode (`single`/`multi`) and whether this person may add one. */
  async list(spaceId: string): Promise<{ space: MediaShowsSpace | null; items: MediaShow[] }> {
    const result = await this.ctx.call<{ space?: MediaShowsSpace; items?: MediaShow[] } | MediaShow[]>({ httpMethod: 'GET', path: '/media-shows' }, { query: { spaceId } })
    if (Array.isArray(result)) return { space: null, items: result }
    return { space: result?.space ?? null, items: result?.items ?? [] }
  }

  async get(id: string): Promise<MediaShow> {
    return this.ctx.call({ httpMethod: 'GET', path: '/media-shows/{id}' }, { id })
  }

  async create(body: MediaShowInput & { spaceId: string; title: string; presenterIds?: string[] }): Promise<MediaShow> {
    return this.ctx.call({ httpMethod: 'POST', path: '/media-shows', hasBody: true }, { body })
  }

  async update(id: string, body: MediaShowInput): Promise<MediaShow> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/media-shows/{id}', hasBody: true }, { id, body })
  }

  async remove(id: string): Promise<void> {
    await this.ctx.call({ httpMethod: 'DELETE', path: '/media-shows/{id}' }, { id })
  }

  async addPresenter(id: string, memberId: string): Promise<MediaShow> {
    return this.ctx.call({ httpMethod: 'POST', path: '/media-shows/{id}/presenters', hasBody: true }, { id, body: { memberId } })
  }

  async removePresenter(id: string, memberId: string): Promise<MediaShow> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/media-shows/{id}/presenters/{memberId}' }, { id, memberId })
  }

  /** Team numbers of the show (owner, admin, moderator, finance, its presenters). */
  async stats(id: string): Promise<MediaShowStats> {
    return this.ctx.call({ httpMethod: 'GET', path: '/media-shows/{id}/stats' }, { id })
  }

  /** "Meu link do feed" (created on the first call): 403 `feedRequiresAccess`, 409 with the RSS off. */
  async myFeed(id: string): Promise<MediaMyFeed> {
    return this.ctx.call({ httpMethod: 'GET', path: '/media-shows/{id}/my-feed' }, { id })
  }

  /** "Gerar outro link": a new one, and the previous stops working at once. */
  async rotateMyFeed(id: string): Promise<MediaMyFeed> {
    return this.ctx.call({ httpMethod: 'POST', path: '/media-shows/{id}/my-feed/rotate' }, { id })
  }

  /** Owner and admin: a show out of another host's RSS feed (400 on `url` when it is not a podcast feed). */
  async importFromRss(body: { spaceId: string; url: string }): Promise<MediaShowImportResult> {
    return this.ctx.call({ httpMethod: 'POST', path: '/media-shows/import', hasBody: true }, { body })
  }

  /** Owner and admin: the show's Store product (created here for a show older than 1b). Price and status by `sdk.products.update`. */
  async product(id: string): Promise<MediaShowProduct> {
    return this.ctx.call({ httpMethod: 'GET', path: '/media-shows/{id}/product' }, { id })
  }

  /** Owner and admin: an event's live recording becomes a draft episode (the file is reused, not copied). */
  async episodeFromEvent(showId: string, body: { eventId: string; title?: string; description?: string | null; season?: number; number?: number; type?: MediaEpisodeType }): Promise<MediaEpisode> {
    return this.ctx.call({ httpMethod: 'POST', path: '/media-shows/{id}/episodes/from-event', hasBody: true }, { id: showId, body })
  }

  /** A page of episodes; `seasons` sits beside `data`, so the whole body is read. */
  async episodes(showId: string, query: { season?: number; status?: MediaEpisodeStatus; order?: 'newest' | 'oldest'; page?: number; limit?: number } = {}): Promise<MediaEpisodePage> {
    const clean = Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== null))
    const body = await this.ctx.envelope<{ data?: MediaEpisode[]; pagination?: MediaPagination; seasons?: number[] }>(
      { httpMethod: 'GET', path: '/media-shows/{id}/episodes' }, { id: showId, query: clean }
    )
    return { items: body?.data ?? [], pagination: body?.pagination ?? null, seasons: body?.seasons ?? [] }
  }

  /** A new episode, always a draft. */
  async createEpisode(showId: string, body: MediaEpisodeInput & { title: string }): Promise<MediaEpisode> {
    return this.ctx.call({ httpMethod: 'POST', path: '/media-shows/{id}/episodes', hasBody: true }, { id: showId, body })
  }

  async getEpisode(id: string): Promise<MediaEpisodeDetail> {
    return this.ctx.call({ httpMethod: 'GET', path: '/media-episodes/{id}' }, { id })
  }

  async updateEpisode(id: string, body: MediaEpisodeInput): Promise<MediaEpisode> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/media-episodes/{id}', hasBody: true }, { id, body })
  }

  async removeEpisode(id: string): Promise<void> {
    await this.ctx.call({ httpMethod: 'DELETE', path: '/media-episodes/{id}' }, { id })
  }

  /** Now, or at `publishAt` when it is in the future. `notifyByEmail: false` sends no notice. */
  async publishEpisode(id: string, body: { publishAt?: string | null; notifyByEmail?: boolean } = {}): Promise<MediaEpisode> {
    return this.ctx.call({ httpMethod: 'POST', path: '/media-episodes/{id}/publish', hasBody: true }, { id, body })
  }

  async unpublishEpisode(id: string): Promise<MediaEpisode> {
    return this.ctx.call({ httpMethod: 'POST', path: '/media-episodes/{id}/unpublish' }, { id })
  }

  /** "Transcrever": 202 queued; 409 one on its way; 429 no minutes left this month. */
  async transcribe(id: string): Promise<{ episodeId: string; transcript: MediaTranscriptState }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/media-episodes/{id}/transcribe' }, { id })
  }

  /** 404 while there is no transcript ready for the current file. */
  async transcript(id: string): Promise<MediaTranscript> {
    return this.ctx.call({ httpMethod: 'GET', path: '/media-episodes/{id}/transcript' }, { id })
  }

  async episodeStats(id: string): Promise<MediaEpisodeStats> {
    return this.ctx.call({ httpMethod: 'GET', path: '/media-episodes/{id}/stats' }, { id })
  }

  async saveProgress(id: string, body: MediaProgressInput): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/media-episodes/{id}/progress', hasBody: true }, { id, body })
  }

  async react(id: string, type: string): Promise<MediaReactionCount[]> {
    return this.ctx.call({ httpMethod: 'POST', path: '/media-episodes/{id}/react', hasBody: true }, { id, body: { type } })
  }

  async unreact(id: string): Promise<MediaReactionCount[]> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/media-episodes/{id}/react' }, { id })
  }

  async comments(id: string, query: { sort?: 'newest' | 'oldest'; page?: number; limit?: number } = {}): Promise<{ items: MediaEpisodeComment[]; pagination: MediaPagination | null }> {
    return itemsOf<MediaEpisodeComment>(await this.ctx.call({ httpMethod: 'GET', path: '/media-episodes/{id}/comments' }, { id, query }))
  }

  async comment(id: string, body: { comment?: string; parentId?: string; videoMediaId?: string }): Promise<MediaEpisodeComment> {
    return this.ctx.call({ httpMethod: 'POST', path: '/media-episodes/{id}/comments', hasBody: true }, { id, body })
  }

  async editComment(id: string, commentId: string, comment: string): Promise<MediaEpisodeComment> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/media-episodes/{id}/comments/{commentId}', hasBody: true }, { id, commentId, body: { comment } })
  }

  async deleteComment(id: string, commentId: string): Promise<void> {
    await this.ctx.call({ httpMethod: 'DELETE', path: '/media-episodes/{id}/comments/{commentId}' }, { id, commentId })
  }

  async reactToComment(id: string, commentId: string, type: string): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'POST', path: '/media-episodes/{id}/comments/{commentId}/react', hasBody: true }, { id, commentId, body: { type } })
  }

  async unreactToComment(id: string, commentId: string): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/media-episodes/{id}/comments/{commentId}/react' }, { id, commentId })
  }
}
